
import Alpine from 'alpinejs'

export const initSelectRangeSlider = (): void => {
    Alpine.data('selectRangeSlider', (filterKey: string, options: Array<unknown>, min: number, max: number) => ({
        filterKey,
        parentElem: null as HTMLElement | null,
        options,
        absoluteMin: min,
        absoluteMax: max,
        minValue: min,
        maxValue: max,
        minInputChanged: false,
        maxInputChanged: false,
        minEl: null as HTMLInputElement | null,
        maxEl: null as HTMLInputElement | null,

        init() {
             // Initialize from URL parameters if they exist
            this.parentElem = this.$el;

            this.$nextTick(() => {
                const minEl = this.$refs.min as HTMLInputElement | undefined;
                const maxEl = this.$refs.max as HTMLInputElement | undefined;
                if (!this.isCheckboxMode && minEl && maxEl) {
                    this.minEl = minEl;
                    this.maxEl = maxEl;
                    this.loadValuesFromURL();
                }
            });
        },

        get isCheckboxMode() {
            return this.options.length <= 3
        },

        // --- URL loading (best-effort for a few common patterns)
        loadValuesFromURL() {
            const urlParams = new URLSearchParams(window.location.search);

            // Look for parameters in format: price[min][value]= and price[max][value]=
            urlParams.forEach((_value, key) => {
                // Match pattern: filterKey[min][number]= or filterKey[max][number]=
                const minMatch = key.match(new RegExp(`^${this.filterKey}\\[min\\]\\[(\\d+)\\]$`));
                const maxMatch = key.match(new RegExp(`^${this.filterKey}\\[max\\]\\[(\\d+)\\]$`));

                if (minMatch) {
                    const minFromURL = parseInt(minMatch[1]);
                    // Set the actual input value
                    if (this.minEl) {
                        this.minEl.value = minFromURL.toString();
                        this.minValue = minFromURL;
                    }
                }

                if (maxMatch) {
                    const maxFromURL = parseInt(maxMatch[1]);
                    // Set the actual input value
                    if (this.maxEl) {
                        this.maxEl.value = maxFromURL.toString();
                        this.maxValue = maxFromURL;
                    }
                }
            });
        },

        validate() {
            // Prevent min from exceeding max
            if (this.minValue > this.maxValue - 1 && this.maxValue - 1 >= this.absoluteMin) {
                this.minValue = this.maxValue - 1;
            }

            // Prevent max from going below min
            if (this.maxValue < this.minValue + 1 && this.minValue + 1 <= this.absoluteMax) {
                this.maxValue = this.minValue + 1;
            }
        },

        updateMinValue(event: Event) {
            const minInput = event.target as HTMLInputElement;
            if (minInput) {
                const newMin = parseInt(minInput.value);
                if (!isNaN(newMin)) {
                    const slider = this.parentElem?.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement;
                    if (slider) {
                        slider.value = newMin.toString();
                        this.minValue = newMin;
                    }
                    this.minInputChanged = true;
                    this.validate();
                    this.onSliderChange();
                } else if (minInput.value === '') {
                    this.minInputChanged = false;
                }
            }
        },

        updateMaxValue(event: Event) {
            const maxInput = event.target as HTMLInputElement;
            if (maxInput) {
                const newMax = parseInt(maxInput.value);

                if (!isNaN(newMax)) {
                    // Retrieve the slider element to update its value
                    const slider = this.parentElem?.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement;
                    if (slider) {
                        slider.value = newMax.toString();
                        this.maxValue = newMax;
                    }
                    this.maxInputChanged = true;
                    this.validate();
                    this.onSliderChange();
                } else if (maxInput.value === '') {
                    this.maxInputChanged = false;
                }
            }
        },

        markMinInputChanged() {
            this.minInputChanged = true;
        },

        markMaxInputChanged() {
            this.maxInputChanged = true;
        },

        clearMinInputIfUnchanged(event: Event) {
            const input = event.target as HTMLInputElement;
            if (!this.minInputChanged && input.value !== '') {
                input.value = '';
            }
        },

        restoreMinInputIfEmpty(event: Event) {
            const input = event.target as HTMLInputElement;
            if (!this.minInputChanged && input.value === '') {
                input.value = this.minValue.toString();
            }
        },

        clearMaxInputIfUnchanged(event: Event) {
            const input = event.target as HTMLInputElement;
            if (!this.maxInputChanged && input.value !== '') {
                input.value = '';
            }
        },

        restoreMaxInputIfEmpty(event: Event) {
            const input = event.target as HTMLInputElement;
            if (!this.maxInputChanged && input.value === '') {
                input.value = this.maxValue.toString();
            }
        },

        resetSlider() {
            this.minValue = this.absoluteMin;
            this.maxValue = this.absoluteMax;
            this.minInputChanged = false;
            this.maxInputChanged = false;

            if (this.minEl) this.minEl.value = this.absoluteMin.toString();
            if (this.maxEl) this.maxEl.value = this.absoluteMax.toString();
        },

        onSliderChange() {
            // Trigger parent's applyFilters
            this.$dispatch('range-slider-change', {
              filterKey: this.filterKey,
              min: this.minValue,
              max: this.maxValue
            });
        },

        get fillStyle(): string {
            const minPercent = ((this.minValue - this.absoluteMin) / (this.absoluteMax - this.absoluteMin)) * 100;
            const maxPercent = ((this.maxValue - this.absoluteMin) / (this.absoluteMax - this.absoluteMin)) * 100;

            return `left: ${minPercent}%; right: ${100 - maxPercent}%;`;
        }
    }))
}
